import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BaseProvider } from '../../../shared/providers/base-provider';
import { AuthNotifier } from './auth-notifier';
import { AuthState } from './auth-state';
import { AuthService } from './auth-service';
import { SessionTimeoutProvider } from './session-timeout/session-timeout-provider';
import type { SessionExpiryReason } from './session-timeout/session-timeout-notifier';
import { SessionCleanupService } from '../../../shared/services/session-cleanup-service';
import { JwtUtil } from '../../../shared/utils/jwt.util';

export const SESSION_EXPIRED_MESSAGE =
  'Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente.';
export const SESSION_TOKEN_EXPIRED_MESSAGE =
  'Su sesión ha expirado por motivos de seguridad. Por favor, inicie sesión nuevamente.';

@Injectable({ providedIn: 'root' })
export class AuthProvider extends BaseProvider<AuthNotifier, AuthState> {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly sessionTimeout = inject(SessionTimeoutProvider);
  private readonly sessionCleanup = inject(SessionCleanupService);
  private isLoggingOut = false;

  constructor() {
    super(AuthNotifier);
    this.sessionTimeout.setOnLogoutHandler((reason) => this.logout(reason));
    if (this.state().isAuthenticated) {
      this.restoreSession();
    }
  }

  /**
   * (Re)registra el handler de logout y arranca el temporizador de inactividad.
   * Debe ejecutarse al inicio de CADA sesión: dispose() libera el handler en el
   * logout y, como AuthProvider es singleton root, su constructor no vuelve a
   * ejecutarse tras un re-login (sin esto, "Cerrar sesión" no logueaba en la
   * segunda sesión).
   */
  private startSessionTimer(): void {
    this.sessionTimeout.setOnLogoutHandler((reason) => this.logout(reason));
    this.sessionTimeout.start();
  }

  private restoreSession(): void {
    const { accessToken } = this.state();

    if (!accessToken) {
      this.startSessionTimer();
      return;
    }

    // Validación de expiración real del JWT al restaurar. Si el token ya venció
    // (p. ej. la pestaña estuvo abierta más de lo que dura el token), NO cargamos
    // el viewer: cerramos sesión limpio con mensaje en vez de morir con un 401.
    const tokenExpiryMs = JwtUtil.getExpiryMs(accessToken);
    if (tokenExpiryMs !== null && tokenExpiryMs <= Date.now()) {
      this.logout('token-expired');
      return;
    }

    this.authService.setAccessToken(accessToken);
    this.sessionTimeout.setTokenExpiryMs(tokenExpiryMs);
    this.startSessionTimer();
  }

  setUsername(username: string): void {
    this._notifier.setUsername(username);
  }

  setPassword(password: string): void {
    this._notifier.setPassword(password);
  }

  setAlias(alias: string): void {
    this._notifier.setAlias(alias);
  }

  setRecaptchaToken(token: string | null): void {
    this._notifier.setRecaptchaToken(token);
  }

  /** Initialize session from internal login redirect (token from MiArbolink via URL) */
  initializeFromToken(token: string): void {
    this.authService.setAccessToken(token);
    this._notifier.setInternalLogin(token, token);
    this.sessionTimeout.setTokenExpiryMs(JwtUtil.getExpiryMs(token));
    this.startSessionTimer();
  }

  /** Initialize session from internal login redirect with full user data (base64 encoded) */
  initializeFromRedirect(userData: any, fallbackToken?: string): void {
    const accessToken = userData.accessToken ?? userData.token ?? fallbackToken;
    this.authService.setAccessToken(accessToken);
    this._notifier.setLoginResult({ ...userData, token: userData.token ?? fallbackToken, accessToken });
    this.sessionTimeout.setTokenExpiryMs(JwtUtil.getExpiryMs(accessToken));
    this.startSessionTimer();
  }

  async login(): Promise<void> {
    this._notifier.setLoading(true);
    this._notifier.setError(null);

    const { username, password, alias, recaptchaToken } = this.state();

    if (!username || !password) {
      this._notifier.setError('Usuario y contraseña son requeridos');
      this._notifier.setLoading(false);
      return;
    }

    try {
      const response = await this.authService.externalLogin(username, password, alias, recaptchaToken ?? undefined);
      this.authService.setAccessToken(response.accessToken);
      this._notifier.setLoginResult(response);
      this.sessionTimeout.setTokenExpiryMs(JwtUtil.getExpiryMs(response.accessToken));
      this.startSessionTimer();
      await this.router.navigateByUrl('/');
    } catch (error: any) {
      const message = this.getErrorMessage(error);
      this._notifier.setError(message);
    } finally {
      this._notifier.setLoading(false);
    }
  }

  private getErrorMessage(error: any): string {
    // 1) Prefer the backend message (e.g. "Usuario no se encuentra habilitado a este sistema")
    const backendMessage = error?.response?.data?.message;
    if (backendMessage) {
      return backendMessage;
    }

    // 2) Fallback manual messages only when the backend did not provide one
    if (error?.code === 'ECONNABORTED') {
      return 'La solicitud tardó demasiado. Verifique su conexión e intente nuevamente.';
    }
    if (error?.response?.status === 401) {
      return 'Credenciales inválidas. Verifique su usuario y contraseña.';
    }
    if (error?.response?.status === 403) {
      return 'No tiene permisos para acceder al sistema.';
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'No se puede conectar con el servidor. Verifique su conexión de red.';
    }
    return error?.message ?? 'Error al iniciar sesión. Verifique sus credenciales.';
  }

  logout(reason: SessionExpiryReason = 'manual'): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    this.authService.clearAccessToken();
    this.sessionTimeout.dispose();
    this.sessionCleanup.clear();
    this._notifier.resetForm();

    if (reason === 'expired') {
      this._notifier.setSessionExpiredMessage(SESSION_EXPIRED_MESSAGE);
    } else if (reason === 'token-expired') {
      this._notifier.setSessionExpiredMessage(SESSION_TOKEN_EXPIRED_MESSAGE);
    }

    this.router.navigateByUrl('/login').finally(() => {
      this.isLoggingOut = false;
    });
  }

  clearSession(): void {
    this.authService.clearAccessToken();
    this.sessionTimeout.stop();
    this._notifier.resetForm();
  }
}
